import { Client } from '@/pages/user/api';
import { AccountModule } from '@/store/modules/account';
import { downloadByAuthUrl } from '@/utils/file.util';
import * as Cache from '@/utils/cache.util';
let apiPrefix = 'portal';

export interface ColorConfigItem {
  printerModel: string;
  mediaName: string;
}

export interface ColorConfigOptionsResult {
  printerModelList: string[];
  mediaNameList: string[];
  printerTree?: ColorPrinterOptionItem[];
}

export interface ColorConfigPageQuery {
  keyword?: string;
  printerModelId?: number;
  pageNum: number;
  pageSize: number;
}

export interface ColorPrinterMediaOptionItem {
  id: number;
  mediaName: string;
}

export interface ColorPrinterOptionItem {
  id: number;
  printerModel: string;
  mediaList: ColorPrinterMediaOptionItem[];
}

export interface PrinterModelConfigItem {
  id: number;
  printerModel: string;
  createdAt?: string;
}

export interface MediaConfigItem {
  id: number;
  printerModelId: number;
  printerModel?: string;
  mediaName: string;
  createdAt?: string;
}

export interface ColorProfileItem {
  sn: string;
  fileType: 'ICC' | 'EMX' | 'PDF' | string;
  fileName: string;
  fileUrl: string;
}

export interface ColorProfileDownloadLinkResult {
  downloadUrl: string;
  fileName: string;
  downloadType: 'single' | 'zip' | string;
  needToken: boolean;
}

export interface ColorProfileAdminPageItem extends ColorProfileItem {
  printerModel: string;
  mediaName: string;
  createdAt: string;
}

export interface ColorProfileAdminPageQuery {
  printerModel?: string;
  mediaName?: string;
  fileName?: string;
  pageNum: number;
  pageSize: number;
}

/**
 * 色彩文件列表接口
 */
export const fileList = async (): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/colorManageFile/fileList',
  });
};

/**
 * 校验优惠券接口
 */
export const validCoupon = async (data: any): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/colorManageFile/validCoupon',
    data,
  });
};

/**
 * 创建订单
 */
export const createOrder = async (data: any): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/colorManageFile/createOrder',
    data,
  });
};

/**
 * 查看订单详情
 */
export const orderDetail = async (data: any): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/colorManageFile/orderDetail',
    data,
  });
};

/**
 * 查询色彩管理配置
 */
export const getColorConfigList = async (data?: { printerModel?: string; mediaName?: string }): Promise<ColorConfigItem[] | null> => {
  return await Client.get<ColorConfigItem[]>({
    url: apiPrefix + '/color/config/list',
    data,
  });
};

export const getColorConfigOptions = async (): Promise<ColorConfigOptionsResult | null> => {
  return await Client.get<ColorConfigOptionsResult>({
    url: apiPrefix + '/color/config/options',
  });
};

export const getPrinterModelPage = async (data: ColorConfigPageQuery): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/color/config/printer/page',
    data,
  });
};

export const createPrinterModel = async (data: { printerModel: string }): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/color/config/printer/create',
    data,
  });
};

export const updatePrinterModel = async (data: { id: number; printerModel: string }): Promise<any | null> => {
  return await Client.put<any>({
    url: apiPrefix + '/color/config/printer/update',
    data,
  });
};

export const deletePrinterModel = async (data: { id: number }): Promise<any | null> => {
  return await Client.delete<any>({
    url: apiPrefix + '/color/config/printer/delete',
    data,
  });
};

export const getMediaPage = async (data: ColorConfigPageQuery): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/color/config/media/page',
    data,
  });
};

export const createMedia = async (data: { printerModelId: number; mediaName: string }): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/color/config/media/create',
    data,
  });
};

export const updateMedia = async (data: { id: number; printerModelId: number; mediaName: string }): Promise<any | null> => {
  return await Client.put<any>({
    url: apiPrefix + '/color/config/media/update',
    data,
  });
};

export const deleteMedia = async (data: { id: number }): Promise<any | null> => {
  return await Client.delete<any>({
    url: apiPrefix + '/color/config/media/delete',
    data,
  });
};

/**
 * 新增色彩管理配置
 */
export const addColorConfig = async (data: { printerModel: string; mediaName: string }): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/color/config/add',
    data,
  });
};

/**
 * 查询色彩配置文件列表
 */
export const getColorProfileList = async (data: {
  printerModel: string;
  mediaName: string;
  fileName?: string;
}): Promise<ColorProfileItem[] | null> => {
  return await Client.get<ColorProfileItem[]>({
    url: apiPrefix + '/color/profile/list',
    data,
  });
};

export const getColorProfileDownloadLink = async (data: { sn: string }): Promise<ColorProfileDownloadLinkResult | null> => {
  return await Client.get<ColorProfileDownloadLinkResult>({
    url: apiPrefix + '/color/profile/download/link',
    data,
  });
};

export const getColorProfileZipDownloadLink = async (data: {
  printerModel: string;
  mediaName: string;
  fileName?: string;
}): Promise<ColorProfileDownloadLinkResult | null> => {
  return await Client.get<ColorProfileDownloadLinkResult>({
    url: apiPrefix + '/color/profile/download/zip/link',
    data,
  });
};

/**
 * 后台分页查询色彩配置文件
 */
export const getColorProfilePage = async (data: ColorProfileAdminPageQuery): Promise<any | null> => {
  return await Client.get<any>({
    url: apiPrefix + '/color/profile/page',
    data,
  });
};

/**
 * 删除色彩配置文件
 */
export const deleteColorProfile = async (data: { sn: string }): Promise<any | null> => {
  return await Client.delete<any>({
    url: apiPrefix + '/color/profile/delete',
    data,
  });
};

/**
 * 保存色彩配置文件
 */
export const saveColorProfile = async (data: {
  printerModel: string;
  mediaName: string;
  fileType: string;
  fileName: string;
  fileUrl: string;
}): Promise<any | null> => {
  return await Client.post<any>({
    url: apiPrefix + '/color/profile/save',
    data,
  });
};

/**
 * 一键打包下载色彩配置文件
 */
export const downloadColorProfileZip = (data: { printerModel: string; mediaName: string; fileName?: string }) =>
  new Promise<void>(async (resolve, reject) => {
    const token = AccountModule.token || Cache.getToken('user');
    try {
      const link = await getColorProfileZipDownloadLink(data);
      if (!link || !link.downloadUrl) {
        throw new Error('下载链接获取失败');
      }
      await downloadByAuthUrl(link.downloadUrl, token || '', link.fileName || 'color-profile.zip');
      resolve();
    } catch (error) {
      reject(error);
    }
  });
